// 訊息加密 — 對應 Unity EncryptionHelper（AES-256-CBC + HMAC-SHA256）。
// 封包格式：IV(16) || Ciphertext || HMAC-Tag(32)。使用瀏覽器原生 Web Crypto。
//
// 注意：HMAC 金鑰來源需與後端一致（可能與 AES 同一把或另行派生）。此處以
// 傳入的兩把金鑰處理；接後端時務必確認金鑰派生與位元組順序。

const IV_LENGTH = 16
const TAG_LENGTH = 32

export interface SessionKeys {
  readonly aesKey: CryptoKey
  readonly hmacKey: CryptoKey
}

export async function importAesKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', bufferOf(raw), { name: 'AES-CBC' }, false, [
    'encrypt',
    'decrypt',
  ])
}

export async function importHmacKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', bufferOf(raw), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
    'verify',
  ])
}

export async function encryptPacket(plain: Uint8Array, keys: SessionKeys): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))
  const cipher = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-CBC', iv: bufferOf(iv) }, keys.aesKey, bufferOf(plain)),
  )
  const tagInput = concat(iv, cipher)
  const tag = new Uint8Array(await crypto.subtle.sign('HMAC', keys.hmacKey, bufferOf(tagInput)))
  return concat(iv, cipher, tag)
}

export async function decryptPacket(packet: Uint8Array, keys: SessionKeys): Promise<Uint8Array> {
  if (packet.length < IV_LENGTH + TAG_LENGTH) {
    throw new Error('封包長度不足')
  }
  const iv = packet.subarray(0, IV_LENGTH)
  const tag = packet.subarray(packet.length - TAG_LENGTH)
  const cipher = packet.subarray(IV_LENGTH, packet.length - TAG_LENGTH)

  const valid = await crypto.subtle.verify(
    'HMAC',
    keys.hmacKey,
    bufferOf(tag),
    bufferOf(concat(iv, cipher)),
  )
  if (!valid) {
    throw new Error('HMAC 驗證失敗')
  }
  return new Uint8Array(
    await crypto.subtle.decrypt({ name: 'AES-CBC', iv: bufferOf(iv) }, keys.aesKey, bufferOf(cipher)),
  )
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, p) => sum + p.length, 0)
  const out = new Uint8Array(total)
  let offset = 0
  for (const part of parts) {
    out.set(part, offset)
    offset += part.length
  }
  return out
}

/** 複製為全新 ArrayBuffer，避免 SharedArrayBuffer 型別問題並符合 Web Crypto 要求。 */
export function bufferOf(data: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(data.byteLength)
  new Uint8Array(buffer).set(data)
  return buffer
}
